import {
  Button,
  Collapsible,
  ContentReferences,
  DeleteConfirmButton,
  DevDetailsButton,
  DragList,
  Drawer,
  DrawerProps,
  Flex,
  Grid,
  IconButton,
  InputContainer,
  Menu,
  NumberInput,
  PencilIcon,
  Spacer,
  Text,
  TextField,
  ToggleButton,
  toaster,
} from 'components'
import {
  CursorArrowRaysIcon,
  DocumentTextIcon,
  DotsDragIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from 'components/icons'
import { useAlert, useCurrentUser } from 'components/providers'
import { isEmpty, isEqual } from 'lodash-es'
import { ReactNode, useEffect, useState } from 'react'
import { ITool, ToolInitStep } from 'types'
import { deleteDoc, getDocById, randomString, updateDoc, usePermission, useTheme } from 'utils'
import { useMutateToolRecords } from '../../utils/toolUtils'

interface Props extends Omit<DrawerProps, 'children'> {
  id?: string
}

interface Tool extends Omit<ITool, 'initSteps'> {
  initStepsWithIds: InitStepWithId[]
}

interface InitStepWithId extends Omit<ToolInitStep, 'property'> {
  id: string
  properties: { key: string; value: string }[]
}

export default function ManageToolDrawer({ id, onClose, ...rest }: Props) {
  const permission = usePermission()
  const user = useCurrentUser()
  const { theme } = useTheme()
  const { confirm } = useAlert()
  const { updateToolRecord, removeToolRecord } = useMutateToolRecords()

  const [draft, setDraft] = useState<Tool>()
  const [tool, setTool] = useState<Tool>()
  const [openLastStep, setOpenLastStep] = useState(false)

  useEffect(() => {
    if (!id) return

    getDocById('tools', id).then(tool => {
      if (!tool) return

      const { initSteps, ...restProps } = tool
      const initStepsWithIds =
        initSteps?.map(({ property, ...stepRest }) => {
          const properties = Object.entries(property || {}).map(([key, value]) => ({ key, value }))
          return { ...stepRest, id: randomString(), properties }
        }) || []
      const body = { ...restProps, initStepsWithIds }
      setDraft(body)
      setTool(body)
    })
  }, [id])

  const isDirty = !isEqual(draft, tool)
  const { title, url, initStepsWithIds } = draft || {}
  const isViewer = permission.content === 'viewer'

  function update(body: Partial<Tool>) {
    setDraft(prev => prev && { ...prev, ...body })
  }

  function createInitStep() {
    if (!draft) return

    const newStep: InitStepWithId = {
      id: randomString(),
      action: 'click',
      selector: '',
      properties: [],
    }
    const initStepsWithIds = [...draft.initStepsWithIds, newStep]
    setOpenLastStep(true)
    setDraft({ ...draft, initStepsWithIds })
  }

  function updateInitStep(id: string, body: Partial<InitStepWithId>) {
    setDraft(prev => {
      if (!prev) return prev

      const initStepsWithIds = prev.initStepsWithIds.map(step => {
        return step.id === id ? { ...step, ...body } : step
      })
      return { ...prev, initStepsWithIds }
    })
  }

  function addInitStepProperty(stepId: string) {
    const initStep = draft?.initStepsWithIds.find(step => step.id === stepId)
    if (!initStep) return

    const properties = [...initStep.properties, { key: '', value: '' }]
    updateInitStep(stepId, { properties })
  }

  function removeInitStepProperty(stepId: string, index: number) {
    const initStep = draft?.initStepsWithIds.find(step => step.id === stepId)
    if (!initStep) return

    const properties = initStep.properties.filter((_, i) => i !== index)
    updateInitStep(stepId, { properties })
  }

  function updateInitStepProperty(stepId: string, index: number, key: string, value: string) {
    const initStep = draft?.initStepsWithIds.find(step => step.id === stepId)
    if (!initStep) return

    const properties = initStep.properties.map((property, i) =>
      i === index ? { key, value } : property
    )
    updateInitStep(stepId, { properties })
  }

  async function handleDone() {
    if (!isDirty || !draft) return onClose?.()

    try {
      const { initStepsWithIds, ...rest } = draft
      const initSteps = initStepsWithIds.map(({ id, properties, ...rest }) => {
        const property = properties.reduce((acc, { key, value }) => {
          if (key && value) acc[key] = value
          return acc
        }, {} as Record<string, string>)
        return { ...rest, ...(!isEmpty(property) && { property }) }
      })
      const body = { ...rest, initSteps }
      const updatedTool = await updateDoc(user!.id, 'tools', body)
      updateToolRecord(updatedTool)
      onClose?.()
    } catch (error) {
      toaster.create({ type: 'error', description: 'Failed to update tool' })
    }
  }

  async function handleDelete() {
    if (!id) return
    try {
      await deleteDoc('tools', id)
      removeToolRecord(id)
      onClose?.()
    } catch (error) {
      toaster.create({ type: 'error', description: 'Failed to delete tool' })
    }
  }

  async function handleCancel() {
    if (isDirty) {
      const confirmed = await confirm('Discard changes?')
      if (!confirmed) return
    }
    onClose?.()
  }

  return (
    <Drawer
      headerIcon={<PencilIcon />}
      header="Edit Tool"
      headerActions={
        <>
          <DevDetailsButton
            items={[
              {
                collection: 'tools',
                id,
                label: 'Tool',
                type: 'db',
              },
            ]}
          />
          <Menu
            _hover={{ color: theme === 'light' ? 'primary.500' : undefined }}
            buttonLabel="Actions"
            variant="ghost"
            menuProps={{ positioning: { placement: 'bottom-start' } }}
            options={[
              {
                type: 'custom',
                label: (
                  <DeleteConfirmButton
                    onDelete={handleDelete}
                    width="100%"
                    borderRadius="none"
                    description="This will remove the tool from the workspace."
                  />
                ),
              },
            ]}
          />
        </>
      }
      footer={
        <>
          <Button onClick={handleCancel} variant="outline">
            Cancel
          </Button>
          <Spacer />
          <Button palette="primary" onClick={handleDone}>
            {isDirty ? 'Save' : 'Done'}
          </Button>
        </>
      }
      onClose={handleCancel}
      {...rest}
    >
      <Flex gap={5} direction={'column'} mt={3} height={'95%'}>
        <TextField
          label="Name"
          disabled={isViewer}
          key={id}
          placeholder="Please enter name for tool"
          value={title || ''}
          onChange={e => update({ title: e.target.value })}
          autoFocus
        />
        <TextField
          label="URL"
          disabled={isViewer}
          id="toolName"
          placeholder="Please enter url"
          value={url || ''}
          onChange={e => update({ url: e.target.value })}
        />
        <InputContainer
          label="Initial Steps"
          helpHover="Steps that run when the site first opens (useful for login, etc.)"
        >
          <DragList
            containerProps={{ gap: 4 }}
            items={initStepsWithIds || []}
            onChange={initStepsWithIds => update({ initStepsWithIds })}
            renderItem={({ action, id, properties, selector, value, wait }, _, index) => (
              <Flex gap={2} key={id} flex={1} p={2} borderRadius="sm" background="white">
                <Collapsible
                  defaultOpen={openLastStep && index === (initStepsWithIds?.length ?? 0) - 1}
                  onOpenChange={open => !open && openLastStep && setOpenLastStep(false)}
                  flex={1}
                  label={
                    <Flex alignItems="center" flex={1} gap={2}>
                      <DotsDragIcon cursor="grab" />
                      <Text textStyle="bodySmall">Step {index + 1}</Text>
                      {action === 'click' ? (
                        <CursorArrowRaysIcon color="secondary.500" />
                      ) : (
                        <DocumentTextIcon color="secondary.500" />
                      )}
                      <Spacer />
                      <IconButton
                        aria-label="Delete step"
                        icon={
                          <TrashIcon
                            color="danger.500"
                            onClick={e => {
                              e.preventDefault()
                              update({
                                initStepsWithIds: initStepsWithIds?.filter((_, i) => i !== index),
                              })
                            }}
                            size="sm"
                          />
                        }
                      />
                    </Flex>
                  }
                >
                  <Flex direction="column" mt={2} ml={2} gap={4}>
                    <Grid templateColumns="1fr 2fr" gap={2}>
                      <InputContainer
                        label="Wait Time"
                        helpHover="Time to wait (ms) before performing the action"
                      >
                        <NumberInput
                          value={wait}
                          disabled={isViewer}
                          onChange={wait => updateInitStep(id, { wait })}
                        />
                      </InputContainer>
                      <InputContainer
                        label="Selector"
                        helpHover='The html element selector to find the element. Generally starts with a "#" or "."'
                      >
                        <TextField
                          disabled={isViewer}
                          value={selector}
                          onChange={e => updateInitStep(id, { selector: e.target.value })}
                        />
                      </InputContainer>
                    </Grid>
                    <ToggleButton
                      alignSelf="flex-start"
                      label="Action"
                      helpHover="The action to perform on the element"
                      options={stepOptions}
                      value={action}
                      onChange={action => updateInitStep(id, { action })}
                    />
                    <Collapsible open={action === 'fill'}>
                      <InputContainer
                        label="Value"
                        helpHover="The value to fill in the input field"
                      >
                        <TextField
                          disabled={isViewer}
                          placeholder="Please enter value"
                          value={value}
                          onChange={e => updateInitStep(id, { value: e.target.value })}
                        />
                      </InputContainer>
                    </Collapsible>
                    <InputContainer
                      label="Property modifiers"
                      helpHover="Modify properties of the element"
                    >
                      <Flex direction="column" gap={2}>
                        {properties.map(({ key, value }, index) => (
                          <Flex key={index} gap={2} alignItems="flex-end">
                            <TextField
                              disabled={isViewer}
                              label="Property"
                              value={key}
                              onChange={e =>
                                updateInitStepProperty(id, index, e.target.value, value)
                              }
                            />
                            <TextField
                              disabled={isViewer}
                              label="Value"
                              value={value}
                              onChange={e => updateInitStepProperty(id, index, key, e.target.value)}
                            />
                            <IconButton
                              aria-label="Remove property"
                              icon={<XMarkIcon />}
                              onClick={() => removeInitStepProperty(id, index)}
                            />
                          </Flex>
                        ))}
                        <Button
                          alignSelf="flex-start"
                          variant="outline"
                          leftIcon={<PlusIcon size="sm" />}
                          disabled={properties && Object.keys(properties).some(key => key === '')}
                          onClick={() => addInitStepProperty(id)}
                        >
                          Add Property
                        </Button>
                      </Flex>
                    </InputContainer>
                  </Flex>
                </Collapsible>
              </Flex>
            )}
          />

          <Button
            alignSelf="flex-start"
            variant="outline"
            leftIcon={<PlusIcon size="sm" />}
            onClick={createInitStep}
          >
            Add Step
          </Button>
        </InputContainer>

        <ContentReferences id={id} type="tools" />
      </Flex>
    </Drawer>
  )
}

const stepOptions: { value: ToolInitStep['action']; label: ReactNode }[] = [
  {
    value: 'click',
    label: (
      <Flex alignItems="center" gap={1}>
        <CursorArrowRaysIcon size="sm" />
        <Text textStyle="bodyXSmall">Click</Text>
      </Flex>
    ),
  },
  {
    value: 'fill',
    label: (
      <Flex alignItems="center" gap={1}>
        <DocumentTextIcon size="sm" />
        <Text textStyle="bodyXSmall">Fill</Text>
      </Flex>
    ),
  },
]
